import React, { useMemo } from "react"
import { useQueryClient } from "react-query"
import { useForm } from "react-hook-form"
import { useHistory, useParams } from "react-router-dom"
import {
  Box, Flex, HStack, Link, Stack, Text,
} from "@chakra-ui/react"
import TitleWithSubtitle from "pages/EventShow/Forms/TitleWithSubtitle"
import Modal from "components/dialogs/Modal"
import FullScreenModalNav from "components/dialogs/FullScreenModalNav"
import Icon from "components/elements/Icon"
import { successToast } from "utilities/toasts"
import { slugify } from "utilities/strings"
import { Translations } from "sharedTypes"
import { useAccountConfiguration } from "queries/users"
import { languageTemplatesCacheKey, useLanguageTemplates } from "queries/languageTemplates"
import { patchLanguageTemplate } from "api/languageTemplates"
import useFeatureFlag from "services/featureFlags"
import LabelRedefinition from "./LabelRedefinition"
import useScrollSpy from "./useScrollSpy"

type FeatureFlag = "temporalQrCodes" | "whatsappMessaging" | "kioskMode" | "eventCapacity"

type FieldConfig = {
  key: keyof Translations
  label?: string
  groupLabel?: string
  helperText?: string
  featureFlag?: FeatureFlag
}

type SectionConfig = {
  title: string
  subtitle?: string
  fields: FieldConfig[]
  /** Only shown when this feature flag is enabled — matches the old Terminology page's
   * Communication Preferences gating, plus Kiosk On-Screen Text, which
   * lived exclusively behind the Kiosk Mode tab (never in Terminology) before this PR. */
  featureFlag?: FeatureFlag
}

const SECTIONS: SectionConfig[] = [
  {
    title: "Guest Confirmation Statuses",
    subtitle: "These fields indicate the guest's status on their confirmation page and correspond with a color.",
    fields: [
      { key: "confirmed" },
      { key: "unconfirmed" },
      { key: "waitlisted" },
      { key: "declined" },
    ],
  },
  {
    title: "Form Buttons & Labels",
    subtitle: "These buttons and labels appear across various sections of the registration forms and the Guest Portal.",
    fields: [
      { key: "continue", helperText: "This button appears when multiple forms or tickets are available for the guest to complete." },
      { key: "save", helperText: "This button appears throughout the Guest Portal wherever the guest can save a form or their profile." },
      { key: "edit", helperText: "This option appears if guests can edit their registration pages." },
      { key: "submitted", helperText: "The label showing the original form submission date." },
      { key: "edited", helperText: "If a guest updates their registration, this label indicates the last time it was edited." },
    ],
  },
  {
    title: "Registration Fields",
    subtitle: "Customize the field names that guests see when they register for an event or edit their existing records.",
    fields: [
      { key: "firstName", groupLabel: "Basic Fields" },
      { key: "lastName" },
      { key: "fullName" },
      { key: "emailAddress" },
      { key: "company" },
      { key: "title" },
      { key: "phone" },
      { key: "birthdate" },
      { key: "addressLine1", groupLabel: "Address Fields" },
      { key: "addressLine2" },
      { key: "city" },
      { key: "state" },
      { key: "postalCode" },
      { key: "country" },
    ],
  },
  {
    title: "Other Phrases",
    subtitle: "Customize the other terms or phrases in the Guest Portal.",
    fields: [
      { key: "guest", helperText: "This header separates each guest's registration by number, e.g., Guest 1, Guest 2, etc., when multiple guests are included." },
      { key: "eventActivity", helperText: "The header that appears on the Guest Portal if a guest has any form submissions." },
      { key: "buttonNotWorking", helperText: "This help text appears at the bottom of the email ensuring the button link works across all email clients." },
      { key: "receipt", helperText: "This label appears in guest emails, followed by a link to the form they submitted." },
      { key: "editProfileHeader", helperText: "The help text that appears on top of the Guest Profile Edit page." },
      { key: "editProfile", helperText: "The header and the menu item that allows a guest to edit their profile." },
      { key: "emailNotifications", helperText: "The header and the menu item that allows a guest to change their email subscription settings." },
      { key: "uploadPhoto", helperText: "The header and the button that allows a guest to upload their own photo." },
      { key: "viewMap", helperText: "The label for the link that opens the event location in a map app." },
      {
        key: "capacityReachedMessage", helperText: "Shown to guests on Registration, Ticket, and RSVP forms once the event's capacity is reached.", featureFlag: "eventCapacity",
      },
    ],
  },
  {
    title: "Registration Forms",
    subtitle: "Messaging shown to guests filling out a Registration form.",
    fields: [
      { key: "registrationSubmitButton", label: "Submit Button Label" },
      { key: "registrationSuccessMessage", label: "Success Message" },
      { key: "registrationEditMessage", label: "Form Edited Message" },
      { key: "registrationClosingMessage", label: "Closing Message" },
      { key: "guestRestrictionErrorMessage", label: "Error Message for Guest Restriction" },
    ],
  },
  {
    title: "Ticket Forms",
    subtitle: "Messaging shown to guests filling out a Ticket form.",
    fields: [
      { key: "ticketSubmitButton", label: "Pay Button Label" },
      { key: "payment", label: "Payment Header" },
      { key: "ticketSuccessMessage", label: "Success Message" },
      { key: "ticketEditMessage", label: "Form Edited Message" },
      { key: "ticketClosingMessage", label: "Closing Message" },
      { key: "guestRestrictionErrorMessage", label: "Error Message for Guest Restriction" },
    ],
  },
  {
    title: "RSVP Forms",
    subtitle: "Messaging shown to guests filling out an RSVP form.",
    fields: [
      { key: "rsvpConfirm", label: "Confirm" },
      { key: "rsvpDecline", label: "Decline" },
      { key: "plusOnesHeader", label: "Plus-Ones Header" },
      { key: "rsvpPlusOneLabel", label: "Plus-Ones Registration Label" },
      { key: "rsvpPlusOneYes", label: "Response Yes" },
      { key: "rsvpPlusOneNo", label: "Response No" },
      { key: "rsvpSubmitButton", label: "Submit Button Label" },
      { key: "rsvpSuccessMessage", label: "Success Message" },
      { key: "rsvpEditMessage", label: "Form Edit Message" },
      { key: "rsvpClosingMessage", label: "Closing Message" },
    ],
  },
  {
    title: "Waitlist Forms",
    subtitle: "Messaging shown to guests filling out a Waitlist form.",
    fields: [
      { key: "waitlistSubmitButton", label: "Submit Button Label" },
      { key: "waitlistSuccessMessage", label: "Success Message" },
      { key: "waitlistEditMessage", label: "Form Edited Message" },
      { key: "waitlistClosingMessage", label: "Closing Message" },
      { key: "guestRestrictionErrorMessage", label: "Error Message for Guest Restriction" },
    ],
  },
  {
    title: "Survey Forms",
    subtitle: "Messaging shown to guests filling out a Survey form.",
    fields: [
      { key: "surveySubmitButton", label: "Submit Button Label" },
      { key: "surveySuccessMessage", label: "Success Message" },
      { key: "surveyEditMessage", label: "Form Edited Message" },
      { key: "surveyClosingMessage", label: "Closing Message" },
    ],
  },
  {
    title: "Communication Preferences",
    subtitle: "Customize the email and WhatsApp language shown to guests on registration forms and in the Guest Portal.",
    featureFlag: "whatsappMessaging",
    fields: [
      { key: "emailSubscription", label: "Email Subscription" },
      { key: "whatsappOptInDisclaimer", label: "WhatsApp Opt-In" },
    ],
  },
  {
    title: "QR Code Messaging",
    subtitle: "Help text and directional language for QR codes on confirmation pages and emails.",
    fields: [
      { key: "qrCodeForCheckIn", helperText: "The help text shown below QR codes on confirmation pages and email notifications." },
      { key: "temporalQrCodeForCheckIn", helperText: "Shown when Temporal QR Codes are enabled, guiding guests to access their code.", featureFlag: "temporalQrCodes" },
      {
        key: "temporalQrCodeMessage", helperText: "Shown below animated QR codes on confirmation pages when Temporal QR Codes are enabled.", featureFlag: "temporalQrCodes",
      },
    ],
  },
  {
    title: "Deferred Guest Registrations",
    subtitle: "Customize the messaging shown when someone registers guests on behalf of others.",
    fields: [
      { key: "registerGuestsNow" },
      { key: "yourInformation" },
      { key: "yourInformationDescription" },
    ],
  },
  {
    title: "Kiosk On-Screen Text",
    subtitle: "Customize or translate the messages and button labels shown to guests on the Kiosk.",
    featureFlag: "kioskMode",
    fields: [
      { key: "kioskScanQrCode" },
      { key: "kioskRegister" },
      { key: "kioskCheckedIn" },
      { key: "kioskInvalidQrCode" },
      { key: "kioskDuplicateQrCode" },
      { key: "kioskPrintingBadge" },
      { key: "kioskPrintingError" },
      { key: "kioskAccessRestricted" },
    ],
  },
]

type Props = {
  eventId: string
  backPath: string
}

const EditLanguageTemplatePage = ({ eventId, backPath }: Props) => {
  const { templateId } = useParams<{ templateId: string }>()
  const history = useHistory()
  const queryClient = useQueryClient()
  const featureFlags = useFeatureFlag()

  const { accountConfiguration } = useAccountConfiguration()
  const { data: templates = [] } = useLanguageTemplates(eventId)
  const template = templates.find((t) => t.id === templateId)
  // Shared/offered templates (event: nil) aren't owned by this event — editing one here
  // would silently change it for every event using it, so they're view-only.
  const readOnly = template?.isOffered ?? false

  const sections = useMemo(() => {
    const enabled = ({ featureFlag }: { featureFlag?: FeatureFlag }) => (
      !featureFlag || featureFlags[featureFlag]
    )

    return SECTIONS.filter(enabled).map((section) => ({
      ...section,
      fields: section.fields.filter(enabled),
    }))
  }, [featureFlags])
  const sectionIds = useMemo(
    () => sections.map((section) => slugify(section.title)),
    [sections],
  )

  const {
    control, handleSubmit, formState: { isSubmitting },
  } = useForm<any>({ values: template?.translations })

  const activeSectionId = useScrollSpy(sectionIds)

  const handleClose = () => history.push(backPath)

  if (!template) {
    return null
  }

  const onSubmit = async (values: Translations) => {
    // "" means no override, not a blank override
    const translations = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== ""),
    ) as Translations

    await patchLanguageTemplate(eventId, template.id, { translations })
    queryClient.invalidateQueries(languageTemplatesCacheKey(eventId))
    successToast({ title: "Language Template Saved" })
    handleClose()
  }

  const submit = handleSubmit(onSubmit)

  return (
    <Modal
      show
      size="full"
      hide={handleClose}
      closeOnEsc={false}
      noPadding
      onSubmit={readOnly ? undefined : submit}
      header={(
        <FullScreenModalNav type="header">
          <Text w={1 / 3} fontSize="initial" color="gray.100" noOfLines={1}>{template.name}</Text>
          <Text color="white" fontSize="large" fontWeight="semibold">Language Editor</Text>
          <HStack spacing={4} w={1 / 3} justify="flex-end">
            <Link onClick={handleClose} color="gray.500" fontWeight="bold">
              {readOnly ? "Close" : "Cancel"}
            </Link>
            {!readOnly && (
              <Link
                onClick={() => submit()}
                color="blue.300"
                fontWeight="bold"
                pointerEvents={isSubmitting ? "none" : "auto"}
                noOfLines={1}
              >
                Save Template
                <Icon icon="cancel-circle" ml={2} />
              </Link>
            )}
          </HStack>
        </FullScreenModalNav>
      )}
    >
      <Box bg="gray.50" px={10} py={8}>
        <Flex maxWidth={1000} margin="auto" gap={10} align="flex-start">
          <Stack
            spacing={0}
            position="sticky"
            top={4}
            minW={220}
            flexShrink={0}
            borderWidth={1}
            borderRadius="md"
            bgColor="white"
            overflow="hidden"
          >
            {sections.map((section) => {
              const id = slugify(section.title)
              const isActive = activeSectionId === id

              return (
                <Link
                  key={id}
                  onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                  display="flex"
                  alignItems="center"
                  minH={10}
                  px={4}
                  py={1.5}
                  bg={isActive ? "ui.selectedItemBg" : undefined}
                  borderLeftWidth={2}
                  borderLeftColor={isActive ? "blue.500" : "transparent"}
                  _hover={{ textDecoration: "none", bg: isActive ? "ui.selectedItemBg" : "gray.50" }}
                  transition="background-color 150ms ease-out, border-color 150ms ease-out"
                >
                  <Text fontSize="sm" color={isActive ? "gray.800" : "gray.600"}>
                    {section.title}
                  </Text>
                </Link>
              )
            })}
          </Stack>
          <Box minWidth={460} maxWidth={750}>
            <Stack spacing={10}>
              {sections.map((section) => (
                <Box key={section.title} id={slugify(section.title)}>
                  <TitleWithSubtitle title={section.title} subtitle={section.subtitle} mb={5} />
                  <Box borderRadius="xl" borderWidth={1} bgColor="white" px={6} py={4}>
                    <Stack spacing={6}>
                      {section.fields.map((field) => (
                        <React.Fragment key={field.key}>
                          {field.groupLabel && (
                            <Text fontSize="lg" fontWeight="semibold" mt={2}>{field.groupLabel}</Text>
                          )}
                          <Box>
                            <LabelRedefinition
                              label={field.label}
                              defaultTranslation={accountConfiguration.translations[field.key]}
                              name={field.key}
                              control={control}
                              disabled={readOnly}
                            />
                            {field.helperText && (
                              <Text mt={1}>{field.helperText}</Text>
                            )}
                          </Box>
                        </React.Fragment>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Flex>
      </Box>
    </Modal>
  )
}

export default EditLanguageTemplatePage
